import React, { useState, useEffect } from 'react';
import {
  AlertTriangle,
  Tag,
  Star,
  X,
  ChevronDown,
  Check,
  Info,
  AlertCircle,
  Sparkles,
  ArrowLeft,
  Loader2,
} from 'lucide-react';
import subscriptionApi from '../../services/subscriptionApi';
// import { useToast } from '../../context/ToastContext';

interface CancelSubscriptionModalProps {
  open: boolean;
  onClose: () => void;
  workspaceId: number;
  workspaceName?: string;
  currentPeriodEnd: string | null;
  subscriptionStatus: string;
  onCancelled?: () => void;
  isLifetime?: boolean;
  lifetimeTierName?: string;
  lifetimeNamedUsers?: number;
  lifetimePublicViewUsers?: number;
  api?: {
    getCancellationReasons: () => Array<{ value: string; label: string }>;
    getRetentionOffer: (step: number) => Promise<any>;
    recordRetentionOfferShown: (step: number, reason?: string, reasonDetails?: string) => Promise<void>;
    acceptRetentionOffer: (step: number) => Promise<{ success: boolean; coupon_applied: string }>;
    cancelSubscription: (options: { reason: string; reason_details?: string; immediate?: boolean }) => Promise<any>;
  };
onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

type ModalStep = 'reason' | 'offer' | 'confirm' | 'done';

const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  open,
  onClose,
  workspaceId,
  workspaceName,
  currentPeriodEnd,
  subscriptionStatus,
  onCancelled,
  isLifetime = false,
  lifetimeTierName,
  lifetimeNamedUsers,
  lifetimePublicViewUsers,
  api,
  onSuccess,
  onError,
}) => {
const showSuccess = onSuccess || (() => {});
  const showError = onError || (() => {});
  const [reason, setReason] = useState('');
  const [reasonDetails, setReasonDetails] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalStep, setModalStep] = useState<ModalStep>('reason');

  // Dropdown state
  const [reasonDropdownOpen, setReasonDropdownOpen] = useState(false);

  // Retention offer state
  const [offerStep, setOfferStep] = useState(1);
  const [currentOffer, setCurrentOffer] = useState<any>(null);
  const [hasNextOffer, setHasNextOffer] = useState(false);
  const [offerLoading, setOfferLoading] = useState(false);

  // Button hover states
  const [primaryHovered, setPrimaryHovered] = useState(false);
  const [secondaryHovered, setSecondaryHovered] = useState(false);

  // Input focus states
  const [detailsFocused, setDetailsFocused] = useState(false);

  const isTrial = subscriptionStatus === 'trial';
  const reasons = api ? api.getCancellationReasons() : subscriptionApi.getCancellationReasons();

  const endDate = currentPeriodEnd
    ? new Date(currentPeriodEnd).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'end of your billing period';

  // Calculate trial cancellation date (3 days from now)
  const trialCancelDate = new Date();
  trialCancelDate.setDate(trialCancelDate.getDate() + 3);
  const trialEndDateStr = trialCancelDate.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setReason('');
      setReasonDetails('');
      setModalStep('reason');
      setOfferStep(1);
      setCurrentOffer(null);
      setReasonDropdownOpen(false);
    }
  }, [open]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClick = () => setReasonDropdownOpen(false);
    if (reasonDropdownOpen) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [reasonDropdownOpen]);

  const fetchRetentionOffer = async (step: number) => {
    setOfferLoading(true);
    try {
      const result = api 
        ? await api.getRetentionOffer(step)
        : await subscriptionApi.getRetentionOffer(workspaceId, step);
      if (result.hasOffer && result.offer) {
        setCurrentOffer(result.offer);
        setHasNextOffer(result.hasNextOffer || false);
        if (step === 1) {
          if (api) {
            await api.recordRetentionOfferShown(step, reason, reasonDetails);
          } else {
            await subscriptionApi.recordRetentionOfferShown(workspaceId, step, reason, reasonDetails);
          }
        }
        setModalStep('offer');
      } else {
        setModalStep('confirm');
      }
    } catch (err) {
      console.error('Error fetching retention offer:', err);
      setModalStep('confirm');
    } finally {
      setOfferLoading(false);
    }
  };

  const handleProceedFromReason = async () => {
    if (!reason) {
      showError('Please select a reason for cancellation');
      return;
    }

    if (isTrial) {
      setModalStep('confirm');
    } else {
      await fetchRetentionOffer(1);
    }
  };

  const handleAcceptOffer = async () => {
    setLoading(true);
    try {
      if (api) {
        await api.acceptRetentionOffer(offerStep);
      } else {
        await subscriptionApi.acceptRetentionOffer(workspaceId, offerStep);
      }
      showSuccess('Offer applied successfully!');
      handleClose();
      if (onCancelled) onCancelled();
    } catch (err: any) {
      showError(err.message || 'Failed to apply offer');
    } finally {
      setLoading(false);
    }
  };

  const handleDeclineOffer = async () => {
    if (hasNextOffer) {
      const nextStep = offerStep + 1;
      setOfferStep(nextStep);
      await fetchRetentionOffer(nextStep);
    } else {
      setModalStep('confirm');
    }
  };

  const handleConfirmCancel = async () => {
    setLoading(true);

    try {
      const cancelOptions = {
        reason,
        reason_details: reasonDetails || undefined,
        immediate: false,
      };
      if (api) {
        await api.cancelSubscription(cancelOptions);
      } else {
        await subscriptionApi.cancelSubscription(workspaceId, cancelOptions);
      }

      showSuccess(isTrial ? 'Trial cancelled' : 'Subscription cancelled');
      setModalStep('done');
    } catch (err: any) {
      showError(err.message || 'Failed to cancel subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setReasonDetails('');
    setModalStep('reason');
    setOfferStep(1);
    setCurrentOffer(null);
    onClose();
  };

  const getSelectedReasonLabel = () => {
    const found = reasons.find((r) => r.value === reason);
    return found ? found.label : 'Select a reason...';
  };

  if (!open) return null;

  // ============================================
  // DONE SCREEN
  // ============================================
  if (modalStep === 'done') {
    return (
      <ModalOverlay onClose={handleClose}>
        <ModalContainer>
          <ModalHeader
            icon={<Check size={20} strokeWidth={2} />}
            iconBg="var(--v2-success-bg)"
            iconColor="var(--v2-success-dark)"
            title={isTrial ? 'Trial Cancelled' : 'Subscription Cancelled'}
            onClose={handleClose}
          />

          <div style={{ padding: '0 24px 24px' }}>
            {isLifetime ? (
              <Alert type="info" icon={<Star size={16} strokeWidth={1.5} />}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--v2-text-secondary)' }}>
                  Your paid subscription has been cancelled. You'll continue to have full access until{' '}
                  <strong style={{ color: 'var(--v2-text)' }}>{endDate}</strong>.
                </p>
                <p style={{ margin: '8px 0 0', fontSize: '0.875rem', color: 'var(--v2-text-secondary)' }}>
                  After that, you'll return to your{' '}
                  <strong style={{ color: 'var(--v2-text)' }}>{lifetimeTierName || 'Lifetime'}</strong> plan with{' '}
                  {lifetimeNamedUsers} monthly active users and {lifetimePublicViewUsers} public view users.
                </p>
              </Alert>
            ) : (
              <Alert type="info">
                <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--v2-text-secondary)' }}>
                  {isTrial ? (
                    <>
                      Your trial has been cancelled. Your workspace will remain accessible until{' '}
                      <strong style={{ color: 'var(--v2-text)' }}>{trialEndDateStr}</strong>, after which it will be
                      scheduled for deletion.
                    </>
                  ) : (
                    <>
                      Your subscription has been cancelled. You'll continue to have access until{' '}
                      <strong style={{ color: 'var(--v2-text)' }}>{endDate}</strong>.
                    </>
                  )}
                </p>
              </Alert>
            )}

            <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', marginTop: '16px' }}>
              Changed your mind? You can reactivate anytime before your access ends.
            </p>
          </div>

          <ModalFooter>
            <div />
            <PrimaryButton
              onClick={() => {
                handleClose();
                if (onCancelled) onCancelled();
              }}
            >
              Done
            </PrimaryButton>
          </ModalFooter>
        </ModalContainer>
      </ModalOverlay>
    );
  }

  // ============================================
  // RETENTION OFFER SCREEN
  // ============================================
  if (modalStep === 'offer' && currentOffer) {
    return (
      <ModalOverlay onClose={handleClose}>
        <ModalContainer>
          <ModalHeader
            icon={<Tag size={20} strokeWidth={1.5} />}
            iconBg="var(--v2-primary-light)"
            iconColor="var(--v2-primary)"
            title="Before You Go..."
            onClose={handleClose}
          />

          <div style={{ padding: '0 24px 24px' }}>
            <Alert type="success">
              <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--v2-text-secondary)' }}>
                We'd hate to see you leave! Here's a special offer to help:
              </p>
            </Alert>

            {/* Offer Card */}
            <div
              style={{
                marginTop: '20px',
                padding: '24px',
                backgroundColor: 'var(--v2-primary-light)',
                border: '2px solid var(--v2-primary)',
                borderRadius: 'var(--radius-lg)',
                textAlign: 'center',
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  padding: '4px 12px',
                  backgroundColor: 'var(--v2-primary)',
                  color: '#ffffff',
                  borderRadius: 'var(--radius-full)',
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '12px',
                }}
              >
                Special Offer
              </span>

              <h3
                style={{
                  fontSize: '1.25rem',
                  fontWeight: 700,
                  color: 'var(--v2-text)',
                  margin: '0 0 8px 0',
                }}
              >
                {currentOffer.description}
              </h3>

              {currentOffer.coupon_details?.discount_percentage && (
                <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', margin: 0 }}>
                  Save {currentOffer.coupon_details.discount_percentage}% on your subscription
                </p>
              )}
            </div>
          </div>

          <ModalFooter>
            <GhostButton onClick={handleDeclineOffer} disabled={loading || offerLoading}>
              {offerLoading ? <Loader2 size={16} strokeWidth={2} className="spin" /> : 'No Thanks'}
            </GhostButton>
            <PrimaryButton onClick={handleAcceptOffer} disabled={loading}>
              {loading ? <Loader2 size={16} strokeWidth={2} className="spin" /> : 'Accept Offer & Stay'}
            </PrimaryButton>
          </ModalFooter>
        </ModalContainer>
      </ModalOverlay>
    );
  }

  // ============================================
  // CONFIRM SCREEN
  // ============================================
  if (modalStep === 'confirm') {
    return (
      <ModalOverlay onClose={handleClose}>
        <ModalContainer>
          <ModalHeader
            icon={<AlertTriangle size={20} strokeWidth={1.5} />}
            iconBg={isLifetime ? 'var(--v2-warning-bg)' : 'var(--v2-error-bg)'}
            iconColor={isLifetime ? 'var(--v2-warning-dark)' : 'var(--v2-error)'}
            title="Confirm Cancellation"
            onClose={handleClose}
          />

          <div style={{ padding: '0 24px 24px' }}>
            {isLifetime ? (
              <Alert type="warning" icon={<Star size={16} strokeWidth={1.5} />}>
                <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--v2-text-secondary)' }}>
                  Are you sure you want to cancel your paid subscription?{' '}
                  <strong style={{ color: 'var(--v2-text)' }}>{workspaceName}</strong> will remain active until{' '}
                  <strong style={{ color: 'var(--v2-text)' }}>{endDate}</strong>.
                </p>
                <p style={{ margin: '8px 0 0', fontSize: '0.875rem', color: 'var(--v2-text-secondary)' }}>
                  After that, you'll return to your{' '}
                  <strong style={{ color: 'var(--v2-text)' }}>{lifetimeTierName || 'Lifetime'}</strong> plan. Features
                  exclusive to your paid plan will no longer be available.
                </p>
              </Alert>
            ) : (
              <Alert type="error">
                <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--v2-text-secondary)' }}>
                  {isTrial ? (
                    <>
                      Are you sure you want to cancel your trial for{' '}
                      <strong style={{ color: 'var(--v2-text)' }}>{workspaceName}</strong>? Your workspace will be
                      deleted on <strong style={{ color: 'var(--v2-text)' }}>{trialEndDateStr}</strong>.
                    </>
                  ) : (
                    <>
                      Are you sure you want to cancel?{' '}
                      <strong style={{ color: 'var(--v2-text)' }}>{workspaceName}</strong> will remain active until{' '}
                      <strong style={{ color: 'var(--v2-text)' }}>{endDate}</strong>, then be scheduled for deletion.
                    </>
                  )}
                </p>
              </Alert>
            )}

            <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', marginTop: '16px' }}>
              {isLifetime
                ? 'You can reactivate your paid subscription anytime before your access period ends.'
                : 'This action cannot be automatically undone after your access period ends.'}
            </p>
          </div>

          <ModalFooter>
            <SecondaryButton onClick={() => setModalStep('reason')} disabled={loading}>
              <ArrowLeft size={16} strokeWidth={1.5} />
              Go Back
            </SecondaryButton>
            <DangerButton onClick={handleConfirmCancel} disabled={loading} variant={isLifetime ? 'warning' : 'error'}>
              {loading ? <Loader2 size={16} strokeWidth={2} className="spin" /> : 'Yes, Cancel'}
            </DangerButton>
          </ModalFooter>
        </ModalContainer>
      </ModalOverlay>
    );
  }

  // ============================================
  // REASON SELECTION SCREEN (DEFAULT)
  // ============================================
  return (
    <ModalOverlay onClose={handleClose}>
      <ModalContainer>
        <ModalHeader
          icon={<AlertTriangle size={20} strokeWidth={1.5} />}
          iconBg="var(--v2-warning-bg)"
          iconColor="var(--v2-warning-dark)"
          title={isTrial ? 'Cancel Trial' : 'Cancel Subscription'}
          onClose={handleClose}
        />

        <div style={{ padding: '0 24px 24px' }}>
          {/* Info Alert */}
          {isLifetime ? (
            <Alert type="info" icon={<Star size={16} strokeWidth={1.5} />}>
              <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--v2-text-secondary)' }}>
                <strong style={{ color: 'var(--v2-text)' }}>{workspaceName}</strong> will remain active until{' '}
                <strong style={{ color: 'var(--v2-text)' }}>{endDate}</strong>. After that, you'll return to your{' '}
                <strong style={{ color: 'var(--v2-text)' }}>{lifetimeTierName || 'Lifetime'}</strong> plan with{' '}
                {lifetimeNamedUsers} monthly active users and {lifetimePublicViewUsers} public view users.
              </p>
            </Alert>
          ) : isTrial ? (
            <Alert type="warning">
              <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--v2-text-secondary)' }}>
                Cancelling will end your trial immediately.{' '}
                <strong style={{ color: 'var(--v2-text)' }}>{workspaceName}</strong> will be accessible for 3 more
                days, then scheduled for deletion.
              </p>
            </Alert>
          ) : (
            <Alert type="warning">
              <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--v2-text-secondary)' }}>
                <strong style={{ color: 'var(--v2-text)' }}>{workspaceName}</strong> will remain active until{' '}
                <strong style={{ color: 'var(--v2-text)' }}>{endDate}</strong>, then your workspace will be scheduled
                for deletion.
              </p>
            </Alert>
          )}

          {/* Reason Selection */}
          <p
            style={{
              fontSize: '0.875rem',
              color: 'var(--v2-text-tertiary)',
              margin: '20px 0 16px',
            }}
          >
            We're sorry to see you go. Please let us know why you're cancelling so we can improve.
          </p>

          {/* Reason Dropdown */}
          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '0.6875rem',
                fontWeight: 500,
                color: 'var(--v2-text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                marginBottom: '6px',
              }}
            >
              Reason for cancellation
            </label>
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setReasonDropdownOpen(!reasonDropdownOpen);
                }}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: 'var(--v2-surface)',
                  border: `1px solid ${reasonDropdownOpen ? 'var(--v2-primary)' : 'var(--v2-border)'}`,
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.875rem',
                  color: reason ? 'var(--v2-text)' : 'var(--v2-text-muted)',
                  textAlign: 'left',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  boxShadow: reasonDropdownOpen ? '0 0 0 3px var(--v2-primary-muted)' : 'none',
                  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                }}
              >
                <span>{getSelectedReasonLabel()}</span>
                <ChevronDown
                  size={16}
                  strokeWidth={1.5}
                  color="var(--v2-text-muted)"
                  style={{
                    transition: 'transform 0.15s ease',
                    transform: reasonDropdownOpen ? 'rotate(180deg)' : 'rotate(0)',
                  }}
                />
              </button>

              {reasonDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '4px',
                    backgroundColor: 'var(--v2-surface)',
                    border: '1px solid var(--v2-border)',
                    borderRadius: 'var(--radius-md)',
                    boxShadow: 'var(--shadow-lg)',
                    zIndex: 50,
                    maxHeight: '200px',
                    overflowY: 'auto',
                    padding: '4px',
                  }}
                >
                  {reasons.map((r) => (
                    <DropdownItem
                      key={r.value}
                      selected={reason === r.value}
                      onClick={() => {
                        setReason(r.value);
                        setReasonDropdownOpen(false);
                      }}
                    >
                      {r.label}
                    </DropdownItem>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Additional Details */}
          {reason && (
            <div style={{ marginBottom: '16px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '0.6875rem',
                  fontWeight: 500,
                  color: 'var(--v2-text-muted)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '6px',
                }}
              >
                {reason === 'other' ? 'Please tell us more' : 'Additional feedback (optional)'}
              </label>
              <textarea
                value={reasonDetails}
                onChange={(e) => setReasonDetails(e.target.value)}
                onFocus={() => setDetailsFocused(true)}
                onBlur={() => setDetailsFocused(false)}
                disabled={loading}
                placeholder={reason === 'other' ? '' : "Anything else you'd like us to know?"}
                rows={reason === 'other' ? 3 : 2}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  backgroundColor: 'var(--v2-surface)',
                  border: `1px solid ${detailsFocused ? 'var(--v2-primary)' : 'var(--v2-border)'}`,
                  borderRadius: 'var(--radius-sm)',
                  fontSize: '0.875rem',
                  color: 'var(--v2-text)',
                  outline: 'none',
                  resize: 'vertical',
                  fontFamily: 'var(--font-sans)',
                  boxShadow: detailsFocused ? '0 0 0 3px var(--v2-primary-muted)' : 'none',
                  transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          )}

          {/* Divider */}
          <div style={{ height: '1px', backgroundColor: 'var(--v2-border)', margin: '20px 0' }} />

          {/* What happens section */}
          <div
            style={{
              backgroundColor: 'var(--v2-surface-alt)',
              borderRadius: 'var(--radius-md)',
              padding: '16px',
            }}
          >
            <h4
              style={{
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: 'var(--v2-text)',
                margin: '0 0 12px 0',
              }}
            >
              What happens when you cancel:
            </h4>
            <ul
              style={{
                margin: 0,
                paddingLeft: '20px',
                fontSize: '0.8125rem',
                color: 'var(--v2-text-tertiary)',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
              }}
            >
              {isLifetime ? (
                <>
                  <li>Access continues until {endDate}</li>
                  <li>No further charges will be made</li>
                  <li>You'll return to your {lifetimeTierName || 'Lifetime'} plan</li>
                  <li>Paid-only features will no longer be available</li>
                  <li>You can reactivate anytime before period ends</li>
                </>
              ) : isTrial ? (
                <>
                  <li>Trial ends immediately</li>
                  <li>Access continues for 3 days</li>
                  <li>Workspace deleted after 3 days</li>
                  <li>You can subscribe anytime before deletion</li>
                </>
              ) : (
                <>
                  <li>Access continues until {endDate}</li>
                  <li>No further charges will be made</li>
                  <li>Workspace will be deleted after access ends</li>
                  <li>You can reactivate anytime before deletion</li>
                </>
              )}
            </ul>
          </div>
        </div>

        <ModalFooter>
          <SecondaryButton onClick={handleClose} disabled={loading || offerLoading}>
            {isTrial ? 'Keep Trial' : 'Keep Subscription'}
          </SecondaryButton>
          <DangerButton onClick={handleProceedFromReason} disabled={loading || offerLoading || !reason} variant="error">
            {offerLoading ? <Loader2 size={16} strokeWidth={2} className="spin" /> : 'Continue'}
          </DangerButton>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

// ============================================
// REUSABLE COMPONENTS
// ============================================

const ModalOverlay: React.FC<{ children: React.ReactNode; onClose: () => void }> = ({ children, onClose }) => (
  <div
    onClick={onClose}
    style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
    }}
  >
    <div onClick={(e) => e.stopPropagation()}>{children}</div>
  </div>
);

const ModalContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      backgroundColor: 'var(--v2-surface)',
      borderRadius: 'var(--radius-lg)',
      boxShadow: 'var(--shadow-lg)',
      width: '100%',
      maxWidth: '480px',
      maxHeight: '90vh',
      overflowY: 'auto',
      fontFamily: 'var(--font-sans)',
    }}
  >
    {children}
  </div>
);

const ModalHeader: React.FC<{
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  title: string;
  onClose: () => void;
}> = ({ icon, iconBg, iconColor, title, onClose }) => {
  const [closeHovered, setCloseHovered] = useState(false);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '20px 24px',
        borderBottom: '1px solid var(--v2-border)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div
          style={{
            width: '36px',
            height: '36px',
            borderRadius: 'var(--radius-md)',
            backgroundColor: iconBg,
            color: iconColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {icon}
        </div>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--v2-text)', margin: 0 }}>{title}</h2>
      </div>
      <button
        onClick={onClose}
        onMouseEnter={() => setCloseHovered(true)}
        onMouseLeave={() => setCloseHovered(false)}
        style={{
          width: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: closeHovered ? 'var(--v2-surface-hover)' : 'transparent',
          border: 'none',
          borderRadius: 'var(--radius-sm)',
          cursor: 'pointer',
          color: 'var(--v2-text-muted)',
          transition: 'background-color 0.15s ease',
        }}
      >
        <X size={18} strokeWidth={1.5} />
      </button>
    </div>
  );
};

const ModalFooter: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px 24px',
      borderTop: '1px solid var(--v2-border)',
      backgroundColor: 'var(--v2-surface-alt)',
    }}
  >
    {children}
  </div>
);

const Alert: React.FC<{
  type: 'info' | 'warning' | 'error' | 'success';
  children: React.ReactNode;
  icon?: React.ReactNode;
}> = ({ type, children, icon }) => {
  const styles = {
    info: { bg: 'var(--v2-info-bg)', border: '#93c5fd', iconColor: 'var(--v2-info-dark)' },
    warning: { bg: 'var(--v2-warning-bg)', border: '#fcd34d', iconColor: 'var(--v2-warning-dark)' },
    error: { bg: 'var(--v2-error-bg)', border: '#fca5a5', iconColor: 'var(--v2-error)' },
    success: { bg: 'var(--v2-success-bg)', border: '#86efac', iconColor: 'var(--v2-success-dark)' },
  };

  const defaultIcons = {
    info: <Info size={16} strokeWidth={1.5} />,
    warning: <AlertTriangle size={16} strokeWidth={1.5} />,
    error: <AlertCircle size={16} strokeWidth={1.5} />,
    success: <Check size={16} strokeWidth={2} />,
  };

  const s = styles[type];

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '14px 16px',
        backgroundColor: s.bg,
        borderRadius: 'var(--radius-md)',
        border: `1px solid ${s.border}`,
      }}
    >
      <span style={{ color: s.iconColor, flexShrink: 0, marginTop: '1px' }}>{icon || defaultIcons[type]}</span>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
};

const PrimaryButton: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}> = ({ children, onClick, disabled }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '10px 20px',
        backgroundColor: disabled ? 'var(--v2-border)' : hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
        color: disabled ? 'var(--v2-text-muted)' : '#ffffff',
        border: 'none',
        borderRadius: 'var(--radius-md)',
        fontSize: '0.875rem',
        fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background-color 0.15s ease',
      }}
    >
      {children}
    </button>
  );
};

const SecondaryButton: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}> = ({ children, onClick, disabled }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '10px 20px',
        backgroundColor: hovered && !disabled ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
        color: disabled ? 'var(--v2-text-muted)' : 'var(--v2-text)',
        border: '1px solid var(--v2-border)',
        borderRadius: 'var(--radius-md)',
        fontSize: '0.875rem',
        fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background-color 0.15s ease',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
};

const GhostButton: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
}> = ({ children, onClick, disabled }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '10px 20px',
        backgroundColor: 'transparent',
        color: hovered && !disabled ? 'var(--v2-text)' : 'var(--v2-text-tertiary)',
        border: 'none',
        borderRadius: 'var(--radius-md)',
        fontSize: '0.875rem',
        fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'color 0.15s ease',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  );
};

const DangerButton: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'error' | 'warning';
}> = ({ children, onClick, disabled, variant = 'error' }) => {
  const [hovered, setHovered] = useState(false);

  const colors = {
    error: { bg: '#dc2626', hover: '#b91c1c' },
    warning: { bg: '#d97706', hover: '#b45309' },
  };

  const c = colors[variant];

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '10px 20px',
        backgroundColor: disabled ? 'var(--v2-border)' : hovered ? c.hover : c.bg,
        color: disabled ? 'var(--v2-text-muted)' : '#ffffff',
        border: 'none',
        borderRadius: 'var(--radius-md)',
        fontSize: '0.875rem',
        fontWeight: 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background-color 0.15s ease',
      }}
    >
      {children}
    </button>
  );
};

const DropdownItem: React.FC<{
  children: React.ReactNode;
  selected: boolean;
  onClick: () => void;
}> = ({ children, selected, onClick }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        padding: '8px 10px',
        fontSize: '0.875rem',
        color: selected ? 'var(--v2-primary)' : 'var(--v2-text)',
        backgroundColor: selected ? 'var(--v2-primary-light)' : hovered ? 'var(--v2-surface-hover)' : 'transparent',
        border: 'none',
        borderRadius: 'var(--radius-sm)',
        width: '100%',
        textAlign: 'left',
        cursor: 'pointer',
        transition: 'background-color 0.1s ease',
        fontWeight: selected ? 500 : 400,
      }}
    >
      <span>{children}</span>
      {selected && <Check size={14} strokeWidth={2} color="var(--v2-primary)" />}
    </button>
  );
};

export default CancelSubscriptionModal;
